import type { GeoJSON, Map as LeafletMap, Point } from 'leaflet';
import { latLngBounds } from 'leaflet';
import { LatLngPipe } from '../pipes/coordinates.pipe';
import { MGA_54_55_THRESHOLD } from '../projections';

/**
 * Parse a single coordinate string (either latitude or longitude)
 * Latitude and longitude values in degrees, minutes, seconds have the format
 * of ddd mm ss.ssss.
 * For example, -38° 14' 16.2532" will be -38 14 16.2532 as an argument.
 * Also, the function accepts decimal degrees format of ddd.dddddd.
 * For example, 38.237570333
 * @param coordinate Coordinate string in DMS or decimal format
 * @returns Parsed decimal degrees or null if parsing fails
 */
export const parseCoordinateString = (coordinate: string): number | null => {
  // Remove any non-numeric characters except for spaces, dots, minus signs
  const cleanedStr = coordinate.trim();

  // Check if it's already in decimal format (contains a decimal point with no spaces)
  if (/^-?\d+\.\d+$/.test(cleanedStr)) {
    return parseFloat(cleanedStr);
  }

  // Try to parse as DMS format
  const parts = cleanedStr.split(/\s+/);

  if (parts.length >= 1 && parts.length <= 3) {
    const degrees = parseFloat(parts[0]);
    if (isNaN(degrees)) {
      console.error('Invalid degrees value:', parts[0]);
      return null;
    }
    const minutes = parts.length >= 2 ? parseFloat(parts[1]) || 0 : 0;
    const seconds = parts.length === 3 ? parseFloat(parts[2]) || 0 : 0;

    // Validate DMS values
    if (minutes < 0 || minutes >= 60 || seconds < 0 || seconds >= 60) {
      console.error(
        'Invalid DMS values: minutes and seconds must be between 0 and 60',
      );
      return null;
    }

    // Convert DMS to decimal degrees
    const sign = degrees < 0 ? -1 : 1;
    const absDegrees = Math.abs(degrees);
    return sign * (absDegrees + minutes / 60 + seconds / 3600);
  }

  console.error('Invalid coordinate format:', coordinate);
  return null;
};

export const isPointGeometryInMGABoundary = (
  geoJsonPoint: GeoJSON.Point,
): boolean => {
  const point = {
    x: geoJsonPoint.coordinates[0],
    y: geoJsonPoint.coordinates[1],
  };
  const latLng = new LatLngPipe().transform(point);
  return Math.abs(latLng.lng) - MGA_54_55_THRESHOLD <= 0.5;
};

export function calculateIdentifyBounds(point: Point, map: LeafletMap) {
  const bufferPixels = 20; // or whatever padding you want

  // Define the top-left and bottom-right corners in pixels
  const topLeft = point.subtract([bufferPixels, bufferPixels]);
  const bottomRight = point.add([bufferPixels, bufferPixels]);

  // Convert to lat/lng
  const southWest = map.containerPointToLatLng(bottomRight);
  const northEast = map.containerPointToLatLng(topLeft);

  // Create bounds
  return latLngBounds(southWest, northEast);
}
